import { MutableRefObject, useCallback, useEffect, useRef } from "react";
import { InputRef } from "antd";

interface UseOtpPasteOptions {
  loading: boolean;
  isSubmitting: MutableRefObject<boolean>;
  inputRefs: MutableRefObject<(InputRef | null)[]>;
  setOtp: React.Dispatch<React.SetStateAction<string[]>>;
  onComplete: (otp: string[]) => Promise<void>;
}

export function useOtpPaste({
  loading,
  isSubmitting,
  inputRefs,
  setOtp,
  onComplete,
}: UseOtpPasteOptions) {

  const onCompleteRef = useRef(onComplete);
  const otpLength: number = 6;
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>, index: number) => {
      event.preventDefault();
      if (isSubmitting.current || loading) return;

      const text = event.clipboardData.getData("text/plain");
      const digits = text.replace(/\D/g, "").slice(0, otpLength);

      if (!digits) return;

      setOtp((prev) => {
        const newOtp = [...prev];

        digits.split("").forEach((digit, i) => {
          const targetIndex = index + i;
          if (targetIndex < otpLength) {
            newOtp[targetIndex] = digit;
          }
        });

        const lastFilledIndex = Math.min(
          index + digits.length - 1,
          otpLength - 1,
        );

        requestAnimationFrame(() => {
          inputRefs.current[lastFilledIndex]?.focus();

          const isComplete = newOtp.every((d) => d !== "");
          if (isComplete) {
            isSubmitting.current = true;
            onCompleteRef.current(newOtp).finally(() => {
              isSubmitting.current = false;
            });
          }
        });

        return newOtp;
      });
    },
    [loading, isSubmitting, inputRefs, setOtp],
  );

  return { handlePaste };
}